/* Si una unidad está activa o dada de baja.
 *
 * EL CAMPO VIENE SUCIO, y no es un detalle: en `estado` conviven cinco formas
 * de decir lo mismo — "1", "true", "0", "false", "" y null. Son restos de
 * migraciones distintas.
 *
 * El código anterior hacía `tr.estado || ...`, y en JavaScript el string
 * "false" NO es falso: es un texto no vacío, o sea verdadero. Resultado: todo
 * lo marcado "0" o "false" se mostraba como Activo. Medido contra la base,
 * eran 106 tractores dados de baja apareciendo como activos.
 *
 * Se devuelven TRES estados y no dos. "Sin dato" existe de verdad —75
 * tractores no tienen nada cargado— y meterlos en cualquiera de los otros dos
 * es inventar. Un padrón que dice "no sé" es más útil que uno que miente
 * parejo.
 */

const ACTIVO = new Set(["1", "true", "si", "sí", "activo"]);
const BAJA = new Set(["0", "false", "no", "baja", "inactivo"]);

export function estadoDeUnidad(valor) {
  const v = String(valor ?? "").trim().toLowerCase();
  if (ACTIVO.has(v)) return "activo";
  if (BAJA.has(v)) return "baja";
  return "sin-dato";
}

export const estaActiva = (valor) => estadoDeUnidad(valor) === "activo";

/* Se exporta para que la pastilla de estado de las tablas no vuelva a
 * escribir estas mismas etiquetas por su cuenta: dos listas iguales en dos
 * archivos distintos se separan en cuanto alguien agrega un estado. */
export const ETIQUETAS_ESTADO = {
  activo: "Activo",
  baja: "De baja",
  vendido: "Vendido",
  chocado: "Chocado",
  irreparable: "Irreparable",
  "sin-actividad": "Sin actividad",
  "sin-dato": "Sin dato",
};

/* ── LOS QUE UNA PERSONA PUEDE DECLARAR ─────────────────────────────────────
 *
 * `sin-actividad` y `sin-dato` NO están, y no es un olvido: el primero es una
 * CONCLUSIÓN nuestra —«el origen lo da por activo y hace un año que no se
 * mueve»— y el segundo es una AUSENCIA. Ninguno de los dos es algo que alguien
 * declare; declarar «sin dato» es borrar la declaración, o sea volver a null.
 *
 * `chocado` e `irreparable` sólo pueden venir de acá: no hay ningún dato del
 * que se deduzcan, y son justo los que se pidieron.
 */
export const ESTADOS_MANUALES = [
  "activo", "baja", "vendido", "chocado", "irreparable",
];

/* La opción que vuelve al estado calculado. Sin esta salida, un camión marcado
 * chocado se queda chocado para siempre aunque vuelva a rodar — misma idea que
 * el tercer paso del orden de la tabla de Documentos, que devuelve al de
 * urgencia. */
export const SIN_DECLARAR = "";

export const OPCIONES_ESTADO_MANUAL = [
  { value: SIN_DECLARAR, label: "Que lo calcule el sistema" },
  ...ESTADOS_MANUALES.map((v) => ({ value: v, label: ETIQUETAS_ESTADO[v] })),
];

const TEXTO = ETIQUETAS_ESTADO;

/* El texto que se muestra. "Sin dato" se dice, no se deja en blanco: una celda
 * vacía se lee como "no aplica" y acá significa "falta cargarlo". */
export function textoDeEstado(valor) {
  return TEXTO[estadoDeUnidad(valor)];
}

/* ---------------------------------------------------------------- tractores
 *
 * De los tractores sabemos más que de los furgones: si están vendidos, y si
 * reportaron kilómetros al GPS de Scania. Eso se resuelve en la base, en
 * `estado_calculado`, y se recalcula después de cada sincronización — así un
 * camión que vuelve a rodar se reactiva solo.
 *
 * Agrega dos estados que en `estado` no se pueden distinguir:
 *
 *   · "Vendido" — un camión vendido y uno simplemente dado de baja llegan los
 *     dos como "false".
 *   · "Sin actividad" — el origen lo da por activo, pero no reporta kilómetros
 *     y su RTO venció hace más de un año. Son 95, con la revisión vencida hace
 *     cuatro años en promedio. No están de baja en ningún lado, pero es
 *     evidente que no ruedan.
 *
 * Y corrige al revés también: hay doce que reportan kilómetros hoy y en el
 * origen figuran de baja.
 *
 * Si `estado_calculado` todavía no está —una fila recién insertada, antes del
 * primer recálculo— se cae al criterio de siempre, así que nunca queda peor
 * que antes.
 */
/* ── Y ARRIBA DE TODO, LO QUE DECLARÓ UNA PERSONA ───────────────────────────
 *
 * UNA DECLARACIÓN LE GANA A UNA INFERENCIA. La regla ya estaba escrita un nivel
 * más abajo, en el recálculo: el estado del origen le gana a lo que nosotros
 * deduzcamos. Ésta es la misma, un escalón más arriba — quien fue a mirar el
 * camión sabe más que el origen y más que la heurística.
 *
 * `estado_manual` es una columna aparte y NO se escribe encima de
 * `estado_calculado`, que es derivada y la reescribe entera
 * `recalcular_tractores_activos()`. Cargar ahí sería guardar algo que se borra
 * esa misma corrida — la trampa de `requiere_peligrosas`, que se guardaba,
 * decía «listo» y amanecía vacía.
 *
 * Y por eso el calculado se sigue pudiendo mirar aunque alguien lo contradiga:
 * son dos respuestas distintas a la misma pregunta y las dos sirven.
 */
export function estadoDeTractor(tractor) {
  const declarado = tractor?.estado_manual;
  if (declarado && TEXTO[declarado]) return declarado;
  const calculado = tractor?.estado_calculado;
  if (calculado && TEXTO[calculado]) return calculado;
  return estadoDeUnidad(tractor?.estado);
}

/* De dónde salió el estado que se está mostrando: `declarado` o `calculado`.
 *
 * Va al lado de la pastilla porque las dos cosas se leen distinto. «Activo»
 * porque el camión reportó kilómetros es un hecho de la telemetría; «Chocado»
 * porque alguien lo cargó es una persona que fue a mirar — y si está mal, hay
 * a quién preguntarle. Sin la distinción, un estado viejo declarado hace un
 * año se lee igual que uno recalculado anoche.
 */
export const origenDeEstado = (tractor) =>
  tractor?.estado_manual && TEXTO[tractor.estado_manual]
    ? "declarado"
    : "calculado";

export const textoDeEstadoTractor = (tractor) => TEXTO[estadoDeTractor(tractor)];

export const tractorEstaActivo = (tractor) =>
  estadoDeTractor(tractor) === "activo";
